/**
 * section-welcome-popup.js — welcome/email-capture modal behavior (SW popup lane, 2026-07-13).
 *
 * Owns: trigger arming (delay or exit-intent), 30-day + session suppression,
 * open/close transitions (respecting prefers-reduced-motion), and a full
 * Tab/Shift+Tab focus trap. Reuses the theme's existing global.js trapFocus() /
 * removeTrapFocus() helpers (same ones details-modal.js uses) instead of
 * reimplementing focus-trap logic — global.js loads (deferred) before this
 * file, since layout/theme.liquid places its <script> earlier in <head> than
 * this section's <script> near the end of <body>.
 *
 * Zero-JS-dependency guarantee for the thing that actually matters: the
 * post-submit WELCOME10 reveal. sections/welcome-popup.liquid renders the
 * success panel already visible (no [hidden], `is-open` class present)
 * server-side when `form.posted_successfully?` is true — so the discount code
 * shows up even if this script fails to load or errors. This file only
 * *enhances* that state (focus trap, close button, suppression bookkeeping);
 * it never gates the reveal itself.
 *
 * Suppression: localStorage timestamp (30 days from dismiss/convert) is the
 * primary mechanism — checked before the trigger is armed at all. A
 * sessionStorage flag is set the moment the popup opens (not just on
 * dismiss) so it can't re-trigger a second time in the same tab session even
 * before the user has acted on it (e.g. a second page's delay timer firing
 * after the popup was already shown once this session).
 */
(function () {
  'use strict';

  if (window.customElements.get('welcome-popup-modal')) return;

  var SUPPRESS_DAYS = 30;
  var SUPPRESS_MS = SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
  var EXIT_INTENT_FALLBACK_MS = 15000; // touch devices never fire mouseleave-to-top

  function storageGet(store, key) {
    try {
      return store.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(store, key, value) {
    try {
      store.setItem(key, value);
    } catch (error) {
      /* storage unavailable (private mode / quota) — fail quiet, never block the popup */
    }
  }

  class SWWelcomePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-welcome-popup-dialog]');
      this.backdrop = this.querySelector('[data-welcome-popup-backdrop]');
      this.closeButtons = Array.prototype.slice.call(this.querySelectorAll('[data-welcome-popup-close]'));
      this.suppressKey = this.dataset.suppressKey || 'sw-welcome-popup';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.triggerEl = null;
      this._opened = false;
      this._cleanupTrigger = null;

      this.closeButtons.forEach(
        function (button) {
          button.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );
      if (this.backdrop) this.backdrop.addEventListener('click', this.close.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      if (this.dataset.postedSuccessfully === 'true') {
        // Already server-rendered open (no [hidden], `is-open` present) — the
        // WELCOME10 code is visible with zero JS dependency. Just enhance:
        // trap focus, mark the conversion suppressed for 30 days.
        this._opened = true;
        this.suppress();
        this.enhanceOpenState();
        return;
      }

      if (this.isSuppressed()) return;

      this.armTrigger();
    }

    disconnectedCallback() {
      if (this._cleanupTrigger) this._cleanupTrigger();
    }

    isSuppressed() {
      var until = storageGet(window.localStorage, this.suppressKey + ':until');
      if (until && Date.now() < parseInt(until, 10)) return true;
      if (storageGet(window.sessionStorage, this.suppressKey + ':shown')) return true;
      return false;
    }

    suppress() {
      storageSet(window.localStorage, this.suppressKey + ':until', String(Date.now() + SUPPRESS_MS));
      storageSet(window.sessionStorage, this.suppressKey + ':shown', '1');
    }

    armTrigger() {
      var trigger = this.dataset.trigger || 'delay';
      var delay = parseInt(this.dataset.delay, 10);
      if (!delay || delay < 0) delay = 4000;

      if (trigger === 'exit_intent') {
        this.armExitIntent(delay);
      } else {
        this.armDelay(delay);
      }
    }

    armDelay(delay) {
      var self = this;
      var timer = setTimeout(function () {
        self.open();
      }, delay);
      this._cleanupTrigger = function () {
        clearTimeout(timer);
      };
    }

    armExitIntent(delay) {
      var self = this;

      function onMouseLeave(event) {
        // Cursor exits through the top edge of the viewport = classic exit-intent.
        if (event.clientY <= 0) teardown(true);
      }

      // Mobile/touch has no mouse, so mouseleave-to-top never fires there — 73%
      // of SW traffic is mobile (CLAUDE.md), so exit-intent alone would show the
      // popup to almost nobody. Fall back to a delayed reveal so touch users
      // still get the offer.
      var fallback = setTimeout(function () {
        teardown(true);
      }, Math.max(delay, EXIT_INTENT_FALLBACK_MS));

      function teardown(shouldOpen) {
        document.removeEventListener('mouseleave', onMouseLeave);
        clearTimeout(fallback);
        self._cleanupTrigger = null;
        if (shouldOpen) self.open();
      }

      document.addEventListener('mouseleave', onMouseLeave);
      this._cleanupTrigger = function () {
        teardown(false);
      };
    }

    open() {
      if (this._opened) return;
      this._opened = true;
      if (this._cleanupTrigger) {
        this._cleanupTrigger();
        this._cleanupTrigger = null;
      }

      this.triggerEl = document.activeElement;
      this.hidden = false;

      if (this.reducedMotion) {
        this.classList.add('is-open');
      } else {
        // Force a layout flush before adding the class so the opacity/transform
        // transition actually runs instead of the element appearing pre-opened.
        // eslint-disable-next-line no-unused-expressions
        this.offsetHeight;
        requestAnimationFrame(
          function () {
            this.classList.add('is-open');
          }.bind(this)
        );
      }

      this.enhanceOpenState();
      storageSet(window.sessionStorage, this.suppressKey + ':shown', '1');
    }

    enhanceOpenState() {
      document.body.classList.add('overflow-hidden');
      var focusTarget =
        this.querySelector('[data-welcome-popup-initial-focus]') ||
        this.querySelector('input:not([type=hidden]), select, button, a[href]') ||
        this.dialog;
      if (typeof window.trapFocus === 'function' && this.dialog) {
        window.trapFocus(this.dialog, focusTarget);
      } else if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus();
      }
    }

    close() {
      if (!this._opened) return;
      this._opened = false;
      this.suppress();

      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');

      if (typeof window.removeTrapFocus === 'function') {
        window.removeTrapFocus(this.triggerEl || null);
      } else if (this.triggerEl && typeof this.triggerEl.focus === 'function') {
        this.triggerEl.focus();
      }

      if (this.reducedMotion) {
        this.hidden = true;
      } else {
        var self = this;
        setTimeout(function () {
          self.hidden = true;
        }, 250);
      }
    }

    onKeydown(event) {
      if (event.code === 'Escape' || event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    }
  }

  window.customElements.define('welcome-popup-modal', SWWelcomePopup);
})();
